import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from "amazon-cognito-identity-js";

const poolData = {
  UserPoolId: "us-east-2_0GgVE6RAr",
  ClientId: "4lc4orq3ff002cs703o10dp9v3"
};
const userPool = new CognitoUserPool(poolData);

const CognitoContext = createContext();

export function CognitoProvider({ children }) {
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [cognitoUserForChange, setCognitoUserForChange] = useState(null);
  const [userAttributesForChange, setUserAttributesForChange] = useState(null);

  // Get current user/session on mount
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }
    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      setIdToken(session.getIdToken().getJwtToken());
      setAccessToken(session.getAccessToken().getJwtToken());
      setRefreshToken(session.getRefreshToken().getToken());
      setIsAuthenticated(true);
      cognitoUser.getUserAttributes((err, attributes) => {
        if (!err && attributes) {
          const userAttributes = {};
          attributes.forEach(attr => {
            userAttributes[attr.Name] = attr.Value;
          });
          setUser({
            username: cognitoUser.getUsername(),
            email: userAttributes.email,
            name: userAttributes.name,
            attributes: userAttributes
          });
        }
        setIsLoading(false);
      });
    });
  }, []);

  // Login function
  const login = useCallback((email, password) => {
    setIsLoading(true);
    setNeedsPasswordChange(false);
    setCognitoUserForChange(null);
    setUserAttributesForChange(null);
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });
      const userData = { Username: email, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: result => {
          // After login, always confirm session and fetch attributes
          const currentUser = userPool.getCurrentUser();
          if (!currentUser) {
            setIsAuthenticated(false);
            setIsLoading(false);
            setUser(null);
            resolve({ idToken: result.getIdToken().getJwtToken() });
            return;
          }
          currentUser.getSession((err, session) => {
            if (err || !session || !session.isValid()) {
              setIsAuthenticated(false);
              setIsLoading(false);
              setUser(null);
              resolve({ idToken: result.getIdToken().getJwtToken() });
              return;
            }
            setIdToken(session.getIdToken().getJwtToken());
            setAccessToken(session.getAccessToken().getJwtToken());
            setRefreshToken(session.getRefreshToken().getToken());
            setIsAuthenticated(true);
            currentUser.getUserAttributes((err, attributes) => {
              if (!err && attributes) {
                const userAttributes = {};
                attributes.forEach(attr => {
                  userAttributes[attr.Name] = attr.Value;
                });
                setUser({
                  username: currentUser.getUsername(),
                  email: userAttributes.email,
                  name: userAttributes.name,
                  attributes: userAttributes
                });
              } else {
                setUser({ username: currentUser.getUsername() });
              }
              setIsLoading(false);
              setNeedsPasswordChange(false);
              setCognitoUserForChange(null);
              setUserAttributesForChange(null);
              resolve({ idToken: session.getIdToken().getJwtToken() });
            });
          });
        },
        onFailure: err => {
          setIsLoading(false);
          setIsAuthenticated(false);
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          setNeedsPasswordChange(true);
          setCognitoUserForChange(cognitoUser);
          setUserAttributesForChange(userAttributes);
          setIsLoading(false);
          setIsAuthenticated(false);
          reject({
            code: "NewPasswordRequired",
            message: "Please set a new password",
            userAttributes,
            requiredAttributes,
            cognitoUser
          });
        }
      });
    });
  }, []);

  // Logout function
  const logout = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setUser(null);
    setIdToken(null);
    setAccessToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    setIsLoading(false);
    setNeedsPasswordChange(false);
    setCognitoUserForChange(null);
    setUserAttributesForChange(null);
  }, []);

  // Get idToken
  const getIdToken = useCallback(() => idToken, [idToken]);
  // Get accessToken
  const getAccessToken = useCallback(() => accessToken, [accessToken]);
  // Get refreshToken
  const getRefreshToken = useCallback(() => refreshToken, [refreshToken]);

  // Complete new password challenge
  const completeNewPasswordChallenge = useCallback((newPassword, userAttributes) => {
    return new Promise((resolve, reject) => {
      if (!cognitoUserForChange) {
        reject(new Error("No Cognito user for password change"));
        return;
      }
      // Remove attributes that can't be modified
      if (userAttributes) {
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;
      }
      cognitoUserForChange.completeNewPasswordChallenge(newPassword, userAttributes, {
        onSuccess: (result) => {
          // After challenge, always confirm session and fetch attributes
          const currentUser = userPool.getCurrentUser();
          if (!currentUser) {
            setIsAuthenticated(false);
            setIsLoading(false);
            setUser(null);
            return resolve({ idToken: result.getIdToken().getJwtToken() });
          }
          currentUser.getSession((err, session) => {
            if (err || !session?.isValid()) {
              setIsAuthenticated(false);
              setIsLoading(false);
              setUser(null);
              return resolve({ idToken: result.getIdToken().getJwtToken() });
            }
            setIdToken(session.getIdToken().getJwtToken());
            setAccessToken(session.getAccessToken().getJwtToken());
            setRefreshToken(session.getRefreshToken().getToken());
            setIsAuthenticated(true);
            currentUser.getUserAttributes((err, attributes) => {
              if (!err && attributes) {
                const userAttributes = {};
                attributes.forEach(attr => {
                  userAttributes[attr.Name] = attr.Value;
                });
                setUser({
                  username: currentUser.getUsername(),
                  email: userAttributes.email,
                  name: userAttributes.name,
                  attributes: userAttributes
                });
              } else {
                setUser({ username: currentUser.getUsername() });
              }
              setIsLoading(false);
              setNeedsPasswordChange(false);
              setCognitoUserForChange(null);
              setUserAttributesForChange(null);
              resolve({ idToken: session.getIdToken().getJwtToken() });
            });
          });
        },
        onFailure: (err) => {
          setIsLoading(false);
          setIsAuthenticated(false);
          reject(err);
        }
      });
    });
  }, [cognitoUserForChange]);

  // Forgot password
  const forgotPassword = useCallback((email) => {
    return new Promise((resolve, reject) => {
      const userData = { Username: email, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.forgotPassword({
        onSuccess: () => resolve({ message: "Password reset code sent to your email" }),
        onFailure: (err) => reject(err)
      });
    });
  }, []);

  // Confirm password (reset with code)
  const confirmPassword = useCallback((email, verificationCode, newPassword) => {
    return new Promise((resolve, reject) => {
      const userData = { Username: email, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => resolve({ message: "Password reset successful" }),
        onFailure: (err) => reject(err)
      });
    });
  }, []);

  // Change password (when logged in)
  const changePassword = useCallback((oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error("No user logged in"));
        return;
      }
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }
        cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve({ message: "Password changed successfully" });
        });
      });
    });
  }, []);

  // Authenticated fetch helper (auto token refresh)
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    let token = idToken;
    if (!token) throw new Error("Not authenticated");
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    };
    try {
      const response = await fetch(url, { ...options, headers });
      // If 401, try to refresh session
      if (response.status === 401) {
        const cognitoUser = userPool.getCurrentUser();
        if (!cognitoUser) throw new Error("No user logged in");
        await new Promise((resolve, reject) => {
          cognitoUser.getSession((err, session) => {
            if (err) return reject(err);
            const refreshToken = session.getRefreshToken();
            cognitoUser.refreshSession(refreshToken, (err, session) => {
              if (err) return reject(err);
              setIdToken(session.getIdToken().getJwtToken());
              setAccessToken(session.getAccessToken().getJwtToken());
              resolve();
            });
          });
        });
        token = idToken;
        headers["Authorization"] = `Bearer ${token}`;
        return fetch(url, { ...options, headers });
      }
      return response;
    } catch (error) {
      throw error;
    }
  }, [idToken]);

  return (
    <CognitoContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      getIdToken,
      getAccessToken,
      getRefreshToken,
      needsPasswordChange,
      cognitoUserForChange,
      userAttributesForChange,
      completeNewPasswordChallenge,
      forgotPassword,
      confirmPassword,
      changePassword,
      authenticatedFetch
    }}>
      {children}
    </CognitoContext.Provider>
  );
}

export function useCognito() {
  return useContext(CognitoContext);
}
